import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, BASE_URL } from '../../components/api/ApiClient';
import JalaliDatePicker from '../../components/common/JalaliDatePicker';
import { gregorianToJalali, jalaliToGregorian, parseJalaliDate } from '../../utils/jalaliDate';

function jalaliStrToGregorian(jalaliStr) {
    if (!jalaliStr) return '';
    const parsed = parseJalaliDate(jalaliStr);
    if (!parsed) return '';
    const g = jalaliToGregorian(parsed.year, parsed.month, parsed.day);
    return `${g.year}-${String(g.month).padStart(2, '0')}-${String(g.day).padStart(2, '0')}`;
}

function gregorianStrToJalali(gregorianStr) {
    if (!gregorianStr) return '';
    try {
        const d = new Date(gregorianStr);
        if (isNaN(d.getTime())) return '';
        const j = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate());
        return `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.day).padStart(2, '0')}`;
    } catch {
        return '';
    }
}

function getTodayJalali() {
    const now = new Date();
    const j = gregorianToJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
    return `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.day).padStart(2, '0')}`;
}

function InlineToast({ toasts }) {
    if (!toasts.length) return null;
    return (
        <div
            style={{
                position: 'fixed',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 9999,
                minWidth: 320,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}
        >
            {toasts.map(t => (
                <div
                    key={t.id}
                    className={`alert mb-0 py-2 px-3 shadow ${t.type === 'success' ? 'alert-success' : 'alert-danger'}`}
                    style={{ direction: 'rtl' }}
                >
                    {t.message}
                </div>
            ))}
        </div>
    );
}

function SaleAccountingDocument({ saleFormId, notificationId, onCancel, showToast: parentShowToast }) {
    const navigate = useNavigate();

    const [docData, setDocData] = useState(null);
    const [docDateJalali, setDocDateJalali] = useState(getTodayJalali());
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [saved, setSaved] = useState(false);
    const [savedDocId, setSavedDocId] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [toasts, setToasts] = useState([]);

    const addToast = (message, type = 'error') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
        if (parentShowToast) parentShowToast(message, type);
    };

    useEffect(() => {
        apiClient(`/purchase/api/sale-accounting-document/?sale_form_id=${saleFormId}`)
            .then(data => {
                setDocData(data);

                if (data.document_date) {
                    setDocDateJalali(gregorianStrToJalali(data.document_date));
                }

                if (data.is_preview === false && data.id != null) {
                    setSaved(true);
                    setSavedDocId(data.id);
                }

                setLoading(false);
            })
            .catch(err => {
                addToast(err.message || 'خطا در بارگذاری سند');
                setLoading(false);
            });
    }, [saleFormId]);

    const formatNumber = (n) => {
        if (n === null || n === undefined) return '0';
        return Number(n).toLocaleString('fa-IR');
    };

    const handleSubmit = async () => {
        if (!docData || submitting || saved) return;

        const sf = docData.sale_form_data || {};
        const sumDebit = sf.total_price || 0;
        const sumCredit = (sf.total_amount || 0) + (sf.total_vat || 0);

        if (Math.abs(sumDebit - sumCredit) > 0.01) {
            addToast('جمع بدهکار و بستانکار برابر نیستند. لطفاً بررسی کنید.', 'error');
            return;
        }

        setSubmitting(true);
        try {
            const gregorianDate = jalaliStrToGregorian(docDateJalali);
            const result = await apiClient('/purchase/api/sale-accounting-document/', {
                method: 'POST',
                data: {
                    sale_form_id: saleFormId,
                    document_date: gregorianDate,
                },
            });
            setDocData(prev => ({ ...prev, ...result, is_preview: false }));
            setSaved(true);
            setSavedDocId(result.id);
            addToast('سند با موفقیت ثبت شد', 'success');
        } catch (err) {
            addToast(err.message || 'خطا در ثبت سند');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePrint = async () => {
        if (!saved || savedDocId == null) {
            addToast('ابتدا سند را ثبت کنید تا بتوانید چاپ بگیرید.', 'error');
            return;
        }

        setPrinting(true);
        try {
            const token = localStorage.getItem('access_token');
            const printUrl = `${BASE_URL}/purchase/api/sale-accounting-document/print/?doc_id=${savedDocId}`;

            const response = await fetch(printUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                const errText = await response.text().catch(() => '');
                throw new Error(`خطای سرور ${response.status}${errText ? ': ' + errText : ''}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const win = window.open(blobUrl, '_blank');
            if (!win) {
                addToast('پاپ‌آپ مرورگر مسدود است. لطفاً آن را در تنظیمات فعال کنید.', 'error');
            }
        } catch (err) {
            addToast(err.message || 'خطا در چاپ سند');
        } finally {
            setPrinting(false);
        }
    };

    const handleCancel = async () => {
        if (submitting || printing) return;
        setSubmitting(true);
        try {
            await apiClient('/purchase/api/sale-form/cancel/', {
                method: 'POST',
                data: { purchase_form_id: saleFormId },
            });
            onCancel();
        } catch (err) {
            addToast(err.message || 'خطا در انصراف');
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                <div className="spinner-border text-primary" />
            </div>
        );
    }

    if (!docData) return null;

    const sf = docData.sale_form_data || {};
    const defaults = docData.account_defaults || {};
    const totalAmount = sf.total_amount || 0;
    const totalVat = sf.total_vat || 0;
    const totalPrice = sf.total_price || 0;
    const sumDebit = totalPrice;
    const sumCredit = totalAmount + totalVat;
    const isBalanced = Math.abs(sumDebit - sumCredit) < 0.01;

    const rows = [
        {
            row: 1,
            kolCode: defaults.counterpart_general_code,
            kolName: defaults.counterpart_general_name,
            moeenCode: defaults.counterpart_subsidiary_code,
            moeenName: defaults.counterpart_subsidiary_name,
            accountCode: defaults.counterpart_account_code,
            accountName: defaults.counterpart_account_name,
            debit: totalPrice,
            credit: 0,
        },
        {
            row: 2,
            kolCode: defaults.vat_general_code,
            kolName: defaults.vat_general_name,
            moeenCode: defaults.vat_subsidiary_code,
            moeenName: defaults.vat_subsidiary_name,
            accountCode: defaults.vat_account_code,
            accountName: defaults.vat_account_name,
            debit: 0,
            credit: totalVat,
        },
        {
            row: 3,
            kolCode: defaults.sale_general_code,
            kolName: defaults.sale_general_name,
            moeenCode: defaults.sale_subsidiary_code,
            moeenName: defaults.sale_subsidiary_name,
            accountCode: defaults.sale_account_code,
            accountName: defaults.sale_account_name,
            debit: 0,
            credit: totalAmount,
        },
    ];

    return (
        <div className="container-fluid" dir="rtl">
            <InlineToast toasts={toasts} />

            <div className="card shadow-sm">
                <div className="card-body">
                    {docData.company_name && (
                        <div className="text-center fw-bold fs-5 mb-1">{docData.company_name}</div>
                    )}
                    <div className="text-center fw-bold fs-6 mb-4">سند حسابداری فروش</div>

                    <div className="row g-3 mb-3">
                        <div className="col-md-3">
                            <label className="form-label fw-bold">شماره سند</label>
                            <input
                                type="text"
                                className="form-control bg-light"
                                value={docData.document_number || ''}
                                disabled
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">شماره عطف</label>
                            <input
                                type="text"
                                className="form-control bg-light"
                                value={docData.reference_number || ''}
                                disabled
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">تاریخ سند</label>
                            <JalaliDatePicker
                                value={docDateJalali}
                                onChange={(val) => setDocDateJalali(val)}
                                disabled={saved}
                                placeholder="انتخاب تاریخ"
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">شرح سند</label>
                            <input
                                type="text"
                                className="form-control bg-light"
                                value={docData.description || 'فروش کالا و خدمت'}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center" style={{ width: 50 }}>ردیف</th>
                                    <th className="text-center">
                                        کد کل
                                        <div className="border-top mt-1 pt-1 text-muted" style={{ fontSize: '11px' }}>عنوان کل</div>
                                    </th>
                                    <th className="text-center">
                                        کد معین
                                        <div className="border-top mt-1 pt-1 text-muted" style={{ fontSize: '11px' }}>عنوان معین</div>
                                    </th>
                                    <th className="text-center">
                                        کد حساب
                                        <div className="border-top mt-1 pt-1 text-muted" style={{ fontSize: '11px' }}>عنوان حساب</div>
                                    </th>
                                    <th className="text-center">شرح ردیف</th>
                                    <th className="text-center">بدهکار</th>
                                    <th className="text-center">بستانکار</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.row}>
                                        <td className="text-center">{r.row}</td>
                                        <td className="text-center">
                                            <div className="fw-bold">{r.kolCode}</div>
                                            <div className="border-top mt-1 pt-1" style={{ fontSize: '11px' }}>{r.kolName}</div>
                                        </td>
                                        <td className="text-center">
                                            <div className="fw-bold">{r.moeenCode}</div>
                                            <div className="border-top mt-1 pt-1" style={{ fontSize: '11px' }}>{r.moeenName}</div>
                                        </td>
                                        <td className="text-center">
                                            <div className="fw-bold">{r.accountCode}</div>
                                            <div className="border-top mt-1 pt-1" style={{ fontSize: '11px' }}>{r.accountName}</div>
                                        </td>
                                        <td className="text-center">فروش کالا و خدمت</td>
                                        <td className="text-center">{formatNumber(r.debit)}</td>
                                        <td className="text-center">{formatNumber(r.credit)}</td>
                                    </tr>
                                ))}
                                <tr className="table-secondary fw-bold">
                                    <td colSpan={5} className="text-center">جمع</td>
                                    <td className={`text-center ${!isBalanced ? 'text-danger' : ''}`}>
                                        {formatNumber(sumDebit)}
                                    </td>
                                    <td className={`text-center ${!isBalanced ? 'text-danger' : ''}`}>
                                        {formatNumber(sumCredit)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {!isBalanced && (
                        <div className="alert alert-danger mb-3">
                            جمع بدهکار ({formatNumber(sumDebit)}) و بستانکار ({formatNumber(sumCredit)}) برابر نیستند.
                        </div>
                    )}

                    {saved && (
                        <div className="alert alert-success mb-3">
                            سند با موفقیت ثبت شده است — شماره سند: <strong>{docData.document_number}</strong>
                        </div>
                    )}

                    <div className="d-flex gap-2 justify-content-between align-items-center">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/invoices/list/')}
                            disabled={submitting || printing}
                        >
                            بازگشت
                        </button>

                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-secondary"
                                onClick={handleCancel}
                                disabled={saved || submitting || printing}
                            >
                                انصراف
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={saved || submitting || printing || !isBalanced}
                            >
                                {submitting && <span className="spinner-border spinner-border-sm me-1" />}
                                ثبت سند
                            </button>

                            <button
                                className="btn btn-info text-white"
                                onClick={handlePrint}
                                disabled={!saved || savedDocId == null || printing || submitting}
                            >
                                {printing && <span className="spinner-border spinner-border-sm me-1" />}
                                چاپ سند
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SaleAccountingDocument;
